import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../components/router/authContext';

const STORAGE_PREFIX = 'efleetplan.guides';
const CURRENT_VERSION = 1;

/**
 * Hook to manage step guide state and persistence
 * @param {string} stepId - Unique identifier for the step (e.g., 'routes', 'depots')
 * @param {Object} content - Guide content with version, title, description, bullets
 * @param {boolean} autoShow - Whether to automatically show guide on mount if not dismissed
 * @returns {Object} Guide state and control functions
 */
export const useStepGuide = (stepId, content, autoShow = true) => {
  const { currentUser, isLoading: authLoading } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  // Get storage key based on user ID or anonymous
  const getStorageKey = useCallback((userId = null) => {
    const id = userId || currentUser?.id || 'anonymous';
    return `${STORAGE_PREFIX}.v${CURRENT_VERSION}.${id}.dismissed`;
  }, [currentUser?.id]);

  // Check if guide should be shown
  const shouldShowGuide = useCallback(() => {
    if (!content || !stepId) return false;

    try {
      const storageKey = getStorageKey();
      const dismissedData = localStorage.getItem(storageKey);
      
      if (!dismissedData) {
        // No dismissed data, show guide
        return true;
      }

      const dismissed = JSON.parse(dismissedData);
      const stepData = dismissed[stepId];

      if (!stepData) {
        // Step not in dismissed list, show guide
        return true;
      }

      // Check if version matches - if content version is newer, show again
      const storedVersion = stepData.version || 0;
      const contentVersion = content.version || CURRENT_VERSION;

      if (contentVersion > storedVersion) {
        // Content updated, show guide again
        return true;
      }

      // User dismissed this version, don't show
      return false;
    } catch (error) {
      console.error('Error checking guide state:', error);
      // On error, show guide to be safe
      return true;
    }
  }, [stepId, content, getStorageKey]);

  // Migrate anonymous dismissals to user-specific storage when user logs in
  const migrateAnonymousDismissals = useCallback(() => {
    if (!currentUser?.id || authLoading) return;

    try {
      const anonymousKey = getStorageKey('anonymous');
      const userKey = getStorageKey(currentUser.id);
      
      const anonymousData = localStorage.getItem(anonymousKey);
      const userData = localStorage.getItem(userKey);
      
      if (anonymousData) {
        const anonymousDismissed = JSON.parse(anonymousData);
        const userDismissed = userData ? JSON.parse(userData) : {};
        
        // Merge anonymous dismissals into user-specific storage
        // User-specific dismissals take precedence
        const merged = { ...anonymousDismissed, ...userDismissed };
        
        // Save merged data to user-specific storage
        localStorage.setItem(userKey, JSON.stringify(merged));
        
        // Clear anonymous storage after migration
        localStorage.removeItem(anonymousKey);
      }
    } catch (error) {
      console.error('Error migrating anonymous dismissals:', error);
    }
  }, [currentUser?.id, authLoading, getStorageKey]);

  // Run migration when user becomes available
  useEffect(() => {
    if (!authLoading && currentUser?.id) {
      migrateAnonymousDismissals();
    }
  }, [authLoading, currentUser?.id, migrateAnonymousDismissals]);

  // Save dismissal state
  const saveDismissal = useCallback((checked) => {
    try {
      // Don't save if auth is still loading (to avoid saving under 'anonymous')
      if (authLoading) {
        return;
      }

      const storageKey = getStorageKey();
      const dismissedData = localStorage.getItem(storageKey);
      const dismissed = dismissedData ? JSON.parse(dismissedData) : {};

      if (checked) {
        // Mark as dismissed with current version
        dismissed[stepId] = {
          version: content?.version || CURRENT_VERSION,
          dismissedAt: new Date().toISOString()
        };
      } else {
        // Remove dismissal (user wants to see it again)
        delete dismissed[stepId];
      }

      localStorage.setItem(storageKey, JSON.stringify(dismissed));
      setDontShowAgain(checked);
    } catch (error) {
      console.error('Error saving guide dismissal:', error);
    }
  }, [stepId, content, getStorageKey, authLoading]);

  // Open guide manually
  const openGuide = useCallback(() => {
    setIsOpen(true);
  }, []);

  // Close guide
  const closeGuide = useCallback(() => {
    setIsOpen(false);
    // Save dismissal state if checkbox was checked
    if (dontShowAgain) {
      saveDismissal(true);
    }
  }, [dontShowAgain, saveDismissal]);

  // Handle "don't show again" checkbox change
  const handleDontShowAgain = useCallback((checked) => {
    setDontShowAgain(checked);
    // Save immediately when checked
    if (checked) {
      saveDismissal(true);
    } else {
      // Also save when unchecked to remove dismissal
      saveDismissal(false);
    }
  }, [saveDismissal]);

  // Auto-show guide on mount if enabled and not dismissed
  // Wait for auth to load before checking/showing to avoid race condition
  useEffect(() => {
    // Don't auto-show if auth is still loading
    if (authLoading) {
      return;
    }

    if (autoShow && shouldShowGuide()) {
      // Small delay to ensure component is fully mounted
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [autoShow, shouldShowGuide, authLoading]);

  return {
    isOpen,
    openGuide,
    closeGuide,
    dontShowAgain,
    onDontShowAgain: handleDontShowAgain,
    shouldShow: shouldShowGuide()
  };
};

